import { Box, Button, Modal, Tooltip, Typography, useMediaQuery, Select, MenuItem, FormControl, InputLabel, TextField, Slide, Alert, AlertTitle } from "@mui/material";
import { useEffect, useState } from "react";
import { useUserContext } from "../../users";
import useAnalytics from "../../../hooks/useAnalytics";
import Loading from "../../../components/Loading";
import { useDealContext } from "../../deal/context";
import ProjectHttpClient from "../../../core/infrastructure/clients/v1/ProjectHttpClient";
import { useAuth0 } from "@auth0/auth0-react";
import { AdvisorBox, ScoreCircle, SummaryContainer } from "../styles";
import { MeetingRequest } from "./meetings/MeetingRequest";
import { CalendarMinus, CalendarPlus, List } from "lucide-react";
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { DateDisplay } from "../../meeting";
import { ExecTabs, ExecTab } from "../styles/AdvisorStyles";
import { SelectChangeEvent } from "@mui/material";
import { AdvisorProfileImage, AdvisorCompany } from "../../advisor";
import { IAdvisor, IProject, IMeeting, IViewpoint, SchedulerType, MeetingStatus, ViewpointStatus, ITouchpoint } from "../../../types";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents";
import { orderMediaResponsesByViewpointResponses, orderMediaResponsesByMultipleViewpoints } from "../../../utils/mediaResponseOrdering";
import { VideoJS } from "../../../components/VideoJs";

const demandOptions = [
    {
        value: "Yes, definitely",
        label: "Very High",
        class: "dark-green"
    },
    {
        value: "Yes, probably",
        label: "High",
        class: "light-green"
    },
    {
        value: "Maybe",
        label: "Medium",
        class: "gray"
    },
    {
        value: "No, probably not",
        label: "Low",
        class: "gray"
    },
    {
        value: "No, definitely not",
        label: "Very Low",
        class: "gray"
    }
]


const cleanHtmlQuestion = (htmlString: string): string => {
    if (!htmlString) return '';

    // Replace br tags (all variations) with spaces
    let cleaned = htmlString.replace(/<br\s*\/?>/gi, ' ');

    // Strip all other HTML tags
    cleaned = cleaned.replace(/<[^>]*>/g, '');

    // Clean up multiple spaces and trim
    cleaned = cleaned.replace(/\s+/g, ' ').trim();

    return cleaned;
};

const MediaAnswer = ({ mediaResponse, touchpoint, advisor, project }: { mediaResponse: any, touchpoint: ITouchpoint | null, advisor: IAdvisor, project: IProject }) => {
    const [showFullSummary, setShowFullSummary] = useState(false);
    const { trackAdvisorEvent } = useAnalytics();
    const handleVideoPlay = (videoUrl: string) => {
        trackAdvisorEvent(advisor, "Watched advisor video", {
            project: project.tag,
            organization: project.organization?.tag,
            touchpoint_uuid: touchpoint?.uuid,
            touchpoint_name: touchpoint?.name,
            video_url: mediaResponse.video_url,
        });
    }

    if (mediaResponse.display_as === "text") {
        return (
            <Typography sx={{
                marginTop: "0.25rem",
                fontSize: "1rem"
            }}>
                {mediaResponse.text}
            </Typography>
        )
    } else {

        return (
            <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "center", flexWrap: "wrap", gap: "40px" }}>
                <Box sx={{ maxWidth: "400px", marginBottom: "20px", flex: "1 1 200px", marginLeft: "20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                    {mediaResponse.display_as === "video" ? (
                        <VideoJS
                            src={mediaResponse.video_url}
                            startAt={0.5}
                            onPlay={() => handleVideoPlay(mediaResponse.video_url || "")}
                            size="small"
                        />
                    ) : (
                        <audio controls style={{ width: "100%", maxWidth: "400px" }}>
                            <source src={mediaResponse.audio_url} type="audio/mp3" />
                        </audio>
                    )}
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
                        <span className="link" onClick={() => { setShowFullSummary(!showFullSummary) }}>Show/hide full transcript</span>
                        {showFullSummary && <Box sx={{ marginTop: "10px", fontSize: "0.9rem", color: "gray" }}>
                            {mediaResponse.text}
                        </Box>}
                    </Box>
                </Box>
                <Box sx={{ height: "auto", flex: "1 1", minWidth: "200px" }}>
                    <div dangerouslySetInnerHTML={{ __html: mediaResponse.summary_html }} />
                </Box>
            </Box>
        )
    }
}

export const AdvisorProfile = ({ advisor, project, updateAdvisor, touchpointUuid }: { advisor: IAdvisor, project: IProject, updateAdvisor: () => void, touchpointUuid: string | null }) => {

    const { userContext } = useUserContext();
    const { trackPage } = useAnalytics();
    const [requestDemo, setRequestDemo] = useState(false);
    const [activeTouchpoint, setActiveTouchpoint] = useState(0);
    const [activeSection, setActiveSection] = useState<string>('highlights');
    const [touchpoints, setTouchpoints] = useState(advisor.viewpoints?.map((viewpoint: IViewpoint) => viewpoint.touchpoint));
    const [viewpoint, setViewpoint] = useState(advisor.viewpoints?.[0]); // @TODO: Add multiple viewpoints
    const [expandedDescription, setExpandedDescription] = useState(false);

    const meetingTypes = [ SchedulerType.INTRO, SchedulerType.DEMO, SchedulerType.TRIAL ];

    dayjs.extend(relativeTime);

    const nextScheduledMeeting = advisor.meetings?.filter((meeting: IMeeting) => meetingTypes.includes(meeting.scheduler?.type as SchedulerType) && meeting.status === "scheduled" && new Date(meeting.event_start!) > new Date())?.sort((a: IMeeting, b: IMeeting) => dayjs(a.created_at!).diff(dayjs(b.created_at!)))?.[0];

    const displayMeeting = nextScheduledMeeting;

    useEffect(() => {
        if (userContext.fetched && userContext.dbUser) {
            trackPage(`deal page - winning - viewpoints - executive`, { organization: project.organization.tag, project: project.tag, role: userContext.projectMemberships.role, viewpoint: viewpoint?.uuid, viewpoint_name: viewpoint?.name });
        }
    }, [userContext]);

    useEffect(() => {
        if (touchpointUuid) {
            const touchpointIndex = advisor.viewpoints?.findIndex((viewpoint: IViewpoint) => viewpoint.touchpoint?.uuid === touchpointUuid);
            if (touchpointIndex !== undefined) {
                setViewpoint(advisor.viewpoints?.[touchpointIndex]);
                setActiveTouchpoint(touchpointIndex + 1);
            }
        }
    }, [touchpointUuid]);

    const handleTouchpointChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveTouchpoint(newValue);
        setViewpoint(advisor.viewpoints?.find((viewpoint: IViewpoint) => viewpoint.touchpoint?.uuid === touchpoints?.[newValue - 1]?.uuid));
        setActiveSection('highlights'); // Reset to highlights when changing touchpoint
    };

    const handleSectionClick = (sectionId: string) => {
        setActiveSection(sectionId);
        // Scroll to position the section at the top of the scrollable container, accounting for tabs height
        const element = document.getElementById(sectionId);
        const container = element?.closest('[data-scrollable-container]');
        if (element && container) {
            const elementOffsetTop = element.offsetTop;
            const containerPaddingTop = 20; // Account for container padding

            // Try to get actual tabs height, fallback to estimated height
            const tabsElement = document.querySelector('[role="tablist"]');
            const tabsHeight = tabsElement ? tabsElement.getBoundingClientRect().height : 60;

            const scrollTop = elementOffsetTop - containerPaddingTop - tabsHeight - 150;
            container.scrollTo({ top: scrollTop, behavior: 'smooth' });
        }
    };

    const responses = viewpoint?.response_json;
    const isSmallScreen = useMediaQuery('(max-width:600px)');
    const isMediumScreen = useMediaQuery('(max-width:900px)');

    const mediaResponses = advisor.viewpoints?.map((viewpoint: IViewpoint) => viewpoint.media_responses?.filter((response: any) => response.display_as === "video" || response.display_as === "audio" || response.display_as === "text" || response.display_as === "hide"))?.flat();
    const highlights = mediaResponses?.filter((response: any) => response.display_as === "video" && response.display_as !== "hide");
    const viewpointHighlights = viewpoint?.media_responses?.filter((response: any) => response.display_as === "video" && response.display_as !== "hide");

    if (!advisor) return null;

    const meetings = advisor.meetings?.filter((meeting: IMeeting) => meetingTypes.includes(meeting.scheduler?.type as SchedulerType) && (meeting.status === MeetingStatus.SCHEDULED || meeting.status === MeetingStatus.COMPLETED))?.sort((a: IMeeting, b: IMeeting) => dayjs(a.event_start!).diff(dayjs(b.event_start!)));

    return (
        <AdvisorBox>
            <Box className="executive-content" sx={{ overflowY: "hidden", height: "100%", display: "flex", flexDirection: "column" }}>
                <Box className="executive-profile">
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "start",
                            alignItems: "start",
                            gap: "20px",
                            padding: "0 20px",
                        }}
                    >
                        <AdvisorProfileImage advisor={advisor} size="medium" />

                        <Box className="viewpoint-profile-details"
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "start",
                                alignItems: "left",

                            }}>
                            <Typography sx={{ fontSize: "1.1rem", textAlign: "left", fontWeight: "700" }}>{advisor?.user?.profile?.name}</Typography>
                            <Typography sx={{
                                fontSize: "1.1rem", textAlign: "left",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                marginBottom: "0.5rem"
                            }}>{advisor?.user?.profile?.title}</Typography>
                            <AdvisorCompany advisor={advisor} style="left" />

                        </Box>
                    </Box>
                    <SummaryContainer>
                        <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" sx={{ padding: "0.5rem", border: "1px solid #ccc", borderRadius: "50px" }}>
                            <Typography variant="body1" sx={{ fontWeight: 900, flexGrow: 1, textAlign: "center", fontSize: "0.875em" }}>
                                Purchasing Interest: {demandOptions.find((option: any) => option.value === advisor?.demand)?.label}
                            </Typography>
                            <ScoreCircle score={advisor?.score || 0}>
                                {advisor?.score || '-'}
                            </ScoreCircle>
                        </Box>
                        {!displayMeeting && <Box>
                            {(!advisor.last_prospect_message_at || dayjs().diff(dayjs(advisor.last_prospect_message_at), 'days') > 30) ? (
                                <Typography className="link" textAlign="center" onClick={() => setRequestDemo(true)} fontSize={14} fontWeight={900}>
                                    <CalendarPlus size={16} strokeWidth={2} /> Request Meeting
                                </Typography>
                            ) : (
                                <Tooltip title={`Meeting can be requested again again in ${30 - dayjs().diff(dayjs(advisor.last_prospect_message_at), 'days')} days`}>
                                    <Typography sx={{ textAlign: "center", fontSize: "1rem", color: "gray" }}>
                                        <CalendarPlus size={16} strokeWidth={2} /> Request Meeting
                                    </Typography>
                                </Tooltip>
                            )}
                            {advisor.last_prospect_message_at ? <Typography sx={{ textAlign: "center", fontSize: "1rem" }}>
                                Last requested <strong>{dayjs(advisor.last_prospect_message_at).fromNow()}</strong></Typography> : null}
                        </Box>
                        }
                        {displayMeeting && <Box>
                            <Box sx={{ textAlign: "center", fontSize: "1rem" }}>
                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                    <DateDisplay meeting={displayMeeting} showTime={false} />
                                    <Box sx={{ textAlign: "left" }}>{nextScheduledMeeting ? <><strong>{project.effective_scheduler_properties?.[nextScheduledMeeting.scheduler?.type as SchedulerType]?.name}</strong><br />Meeting</> : <><strong>{project.effective_scheduler_properties?.[displayMeeting.scheduler?.type as SchedulerType]?.name}</strong><br />Meeting</>}</Box>
                                </Box>
                            </Box>
                        </Box>}
                    </SummaryContainer>
                </Box>
                {(responses || activeTouchpoint === 0) ? (
                    <>
                        {/* Touchpoint Tabs */}
                        {touchpoints && touchpoints.length > 0 && (
                            <ExecTabs
                                value={activeTouchpoint}
                                onChange={handleTouchpointChange}
                                variant="scrollable"
                                scrollButtons={false}
                                sx={{
                                    borderBottom: 1,
                                    borderColor: 'divider',
                                    px: 2,
                                    marginBottom: "0px"
                                }}
                            >
                                <ExecTab
                                    key={0}
                                    label="Overview"
                                    sx={{
                                        fontWeight: 600,
                                        textTransform: 'none',
                                        minWidth: "120px",
                                        width: "auto",
                                        maxWidth: "240px",
                                        padding: "0 10px"
                                    }}
                                />
                                {touchpoints.map((touchpoint: any, index: number) => {
                                    const viewpoint = advisor.viewpoints?.find((viewpoint: IViewpoint) => viewpoint.touchpoint?.uuid === touchpoint.uuid);
                                    return (
                                        <ExecTab
                                            key={index + 1}
                                            label={touchpoint.short_name || touchpoint.name}
                                            sx={{
                                                fontWeight: 600,
                                                textTransform: 'none',
                                                minWidth: "120px",
                                                width: "auto",
                                                maxWidth: "240px",
                                                padding: "0 10px",
                                                color: viewpoint?.status === ViewpointStatus.IN_PROGRESS ? "gray" : viewpoint?.status === ViewpointStatus.REJECTED ? "red" : "black",
                                                "&.Mui-selected": {
                                                    color: viewpoint?.status === ViewpointStatus.IN_PROGRESS ? "gray" : viewpoint?.status === ViewpointStatus.REJECTED ? "red" : "black"
                                                }
                                            }}
                                        />
                                    )
                                })}
                            </ExecTabs>
                        )}

                        {/* Summary */}
                        {activeTouchpoint === 0 ? (
                            <Box id="summary" sx={{ flex: 1, padding: "40px", overflowY: "auto" }}>
                                <Box sx={{ display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between", gap: "30px", flexWrap: "wrap" }}>
                                    <Box sx={{ flex: 1, minWidth: "400px" }}>
                                        <Box sx={{ marginBottom: "40px" }}>
                                            {JSON.parse(advisor.highlights_json || "{}").highlights?.map((highlight: string, index: number) => (
                                                <Typography variant="body1" key={index} sx={{ marginBottom: "10px" }}>{highlight}</Typography>
                                            ))}
                                        </Box>
                                        {(highlights?.length ?? 0) > 0 && (
                                            <Box sx={{ marginBottom: "40px" }}>
                                                <Box sx={{ marginBottom: "10px", paddingBottom: "20px", borderBottom: "1px solid #c9c9c9" }}>
                                                    <h5 style={{ marginBottom: "0", fontSize: "1.2rem", fontWeight: "900" }}>Videos</h5>
                                                </Box>
                                                <Box>
                                                    {orderMediaResponsesByMultipleViewpoints(highlights!, advisor.viewpoints || [])?.map((mediaResponse: any, index: number) => (
                                                        <Box key={mediaResponse.uuid} sx={{ marginBottom: "40px" }}>
                                                            <Typography sx={{
                                                                fontWeight: "700",
                                                                fontSize: "1rem",
                                                                color: "#000000"
                                                            }}>
                                                                {cleanHtmlQuestion(mediaResponse.title)}
                                                            </Typography>
                                                            <Box sx={{ borderLeft: "2px solid #ccc", padding: "15px 20px", margin: "15px 0" }}>
                                                                <MediaAnswer mediaResponse={mediaResponse} touchpoint={touchpoints?.[index] || null} advisor={advisor} project={project} />
                                                            </Box>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                    <DisplayBoxBase className="gray column left" sx={{ flex: "1 1 200px", maxWidth: isMediumScreen ? "100%" : "250px" }}>
                                        <h5 style={{ marginBottom: "20px" }}>Meetings</h5>
                                        {meetings?.map((meeting: IMeeting, index: number) => (
                                            <Box key={index} sx={{ marginBottom: "10px", display: "flex", flexDirection: "row", alignItems: "flex-start", justifyContent: "space-between", gap: "10px" }}>
                                                <DateDisplay meeting={meeting} showTime={false} />
                                                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "flex-start" }}>
                                                    <Typography sx={{ fontSize: "0.9rem", fontWeight: "700" }}>{project.effective_scheduler_properties?.[meeting.scheduler?.type as SchedulerType]?.name}</Typography>
                                                    <Typography variant="caption" sx={{ fontSize: "0.9rem" }}>{dayjs(meeting.event_start).tz(dayjs.tz.guess()).format("h:mm A z")}</Typography>
                                                </Box>
                                            </Box>
                                        ))}
                                        {meetings?.length === 0 &&
                                            <Box sx={{ marginBottom: "10px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "10px" }}>
                                                <CalendarMinus size={32} color="gray" strokeWidth={2} />
                                                <Typography variant="body1" sx={{ fontSize: "0.9rem", color: "gray" }}>No meetings scheduled</Typography>
                                            </Box>
                                        }
                                    </DisplayBoxBase>
                                </Box>
                            </Box>
                        ) : (
                            <>
                                <Box sx={{ display: "flex", height: "100%" }}>
                                    {/* Table of Contents Sidebar */}
                                    {!isMediumScreen && (
                                        <Box sx={{
                                            width: "200px",
                                            borderRight: "1px solid #e0e0e0",
                                            padding: "20px 16px",
                                            backgroundColor: "#fafafa",
                                            overflowY: "auto",
                                            flexShrink: 0
                                        }}>
                                            <Typography variant="h6" sx={{
                                                padding: "8px 12px",
                                                marginBottom: "4px",
                                                color: "black",
                                                fontWeight: 700,
                                                fontSize: "1rem"

                                            }}
                                            >
                                                Sections
                                            </Typography>

                                            {/* Highlights section */}
                                            {false && ((viewpointHighlights?.length ?? 0) > 0) && (
                                                <Box
                                                    onClick={() => handleSectionClick('highlights')}
                                                    sx={{
                                                        padding: "8px 12px",
                                                        marginBottom: "4px",
                                                        borderRadius: "6px",
                                                        cursor: "pointer",
                                                        backgroundColor: activeSection === 'highlights' ? "#e3f2fd" : "transparent",
                                                        color: activeSection === 'highlights' ? "#1976d2" : "#666",
                                                        fontWeight: activeSection === 'highlights' ? 600 : 400,
                                                        fontSize: "0.9rem",
                                                        "&:hover": {
                                                            backgroundColor: activeSection === 'highlights' ? "#e3f2fd" : "#f0f0f0"
                                                        }
                                                    }}
                                                >
                                                    Highlights
                                                </Box>
                                            )}

                                            {/* Section links */}
                                            {responses.sections.map((section: any, index: number) => (
                                                <Box
                                                    key={index}
                                                    onClick={() => handleSectionClick(`section-${index}`)}
                                                    sx={{
                                                        padding: "8px 12px",
                                                        marginBottom: "4px",
                                                        borderRadius: "6px",
                                                        cursor: "pointer",
                                                        backgroundColor: activeSection === `section-${index}` ? "#e3f2fd" : "transparent",
                                                        color: activeSection === `section-${index}` ? "#1976d2" : "#666",
                                                        fontWeight: activeSection === `section-${index}` ? 600 : 400,
                                                        fontSize: "0.9rem",
                                                        "&:hover": {
                                                            backgroundColor: activeSection === `section-${index}` ? "#e3f2fd" : "#f0f0f0"
                                                        }
                                                    }}
                                                >
                                                    {section.name}
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {/* Main Content Area */}
                                    <Box sx={{ flex: 1, padding: "20px", paddingBottom: "60vh", overflowY: "auto" }} data-scrollable-container>
                                        {viewpoint?.status === ViewpointStatus.REJECTED && (
                                            <Alert severity="error" sx={{ marginBottom: "20px" }}>
                                                <AlertTitle>This viewpoint has been rejected</AlertTitle>
                                            </Alert>
                                        )}
                                        {viewpoint?.status === ViewpointStatus.IN_PROGRESS && (
                                            <Alert severity="info" sx={{ marginBottom: "20px" }}>
                                                <AlertTitle>This viewpoint is still in progress</AlertTitle>
                                            </Alert>
                                        )}
                                        {viewpoint?.status === ViewpointStatus.COMPLETED && (
                                            <Alert severity="success" sx={{ marginBottom: "20px" }}>
                                                <AlertTitle>This viewpoint is ready for review</AlertTitle>
                                            </Alert>
                                        )}
                                        {/* Highlights Section */}
                                        {false &&((viewpointHighlights?.length ?? 0) > 0) && (
                                            <Box id="highlights" sx={{ marginBottom: "40px", marginLeft: "20px" }}>
                                                <Box sx={{ marginBottom: "10px", paddingBottom: "20px", borderBottom: "1px solid #c9c9c9" }}>
                                                    <h5 style={{ marginBottom: "0", fontSize: "1.2rem", fontWeight: "900" }}>Highlights</h5>
                                                </Box>
                                                {orderMediaResponsesByViewpointResponses(viewpointHighlights!, responses)
                                                    .map((response: any, index: number) => (
                                                        <Box key={index} sx={{ marginBottom: "1.5rem" }}>
                                                            <Typography sx={{
                                                                fontWeight: "700",
                                                                fontSize: "1rem",
                                                                color: "#000000"
                                                            }}>
                                                                {cleanHtmlQuestion(response.title)}
                                                            </Typography>
                                                            <Box sx={{ borderLeft: "2px solid #ccc", padding: "20px", margin: "20px 0" }}>
                                                                <MediaAnswer mediaResponse={response} touchpoint={viewpoint?.touchpoint || null} advisor={advisor} project={project} />
                                                            </Box>
                                                        </Box>
                                                    ))
                                                }
                                            </Box>
                                        )}

                                        {/* Sections */}
                                        {responses.sections.map((section: any, sectionIndex: number) => (
                                            <Box key={sectionIndex} id={`section-${sectionIndex}`} sx={{ marginBottom: "80px", marginLeft: "20px" }}>
                                                <Box sx={{ marginBottom: "10px", paddingBottom: "20px", borderBottom: "1px solid #c9c9c9" }}>
                                                    <h5 style={{ marginBottom: "0", fontSize: "1.2rem", fontWeight: "900" }}>{section.name}</h5>
                                                </Box>

                                                {/* Product Description */}
                                                {section.name === viewpoint?.touchpoint?.product_description_section && viewpoint?.touchpoint?.product_description && (
                                                    <Box sx={{ marginBottom: "40px" }}>
                                                        <Typography sx={{
                                                            fontWeight: "700",
                                                            fontSize: "1rem",
                                                            color: "#000000"
                                                        }}>
                                                            Please read the following product description
                                                        </Typography>
                                                        <Box sx={{ borderLeft: "2px solid #ccc", padding: "15px 20px", margin: "15px 0", position: "relative" }}>
                                                            <Box sx={{
                                                                marginTop: "0.25rem",
                                                                fontSize: "1rem",
                                                                maxHeight: expandedDescription ? "none" : "6em",
                                                                overflow: "hidden",
                                                                position: "relative",
                                                            }}>
                                                                <div dangerouslySetInnerHTML={{ __html: viewpoint.touchpoint.product_description }} />
                                                            </Box>
                                                            {!expandedDescription && (
                                                                <Box sx={{
                                                                    position: "absolute",
                                                                    bottom: 0,
                                                                    left: 0,
                                                                    right: 0,
                                                                    height: "100px",
                                                                    background: "linear-gradient(transparent, white)",
                                                                    display: "flex",
                                                                    alignItems: "flex-end",
                                                                    justifyContent: "center",
                                                                    paddingBottom: "10px"
                                                                }}>
                                                                    <Button
                                                                        onClick={() => setExpandedDescription(true)}
                                                                        variant="outlined"
                                                                        sx={{
                                                                            backgroundColor: "white",
                                                                            "&:hover": { backgroundColor: "#f5f5f5" }
                                                                        }}
                                                                    >
                                                                        Expand
                                                                    </Button>
                                                                </Box>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                )}

                                                {/* Questions */}
                                                {section.questions.map((item: any, questionIndex: number) => {
                                                    const mediaMatch = item.answers[0]?.match(/^videoUUID:(.*)$/) || item.answers[0]?.match(/^audioUUID:(.*)$/) || item.answers[1]?.match(/^audioUUID:(.*)$/) || item.answers[0]?.match(/^textUUID:(.*)$/);
                                                    const mediaResponse = mediaMatch ? mediaResponses?.find((response: any) => response.uuid === mediaMatch[1] && (response.display_as === "video" || response.display_as === "audio" || response.display_as === "text" || response.display_as === "hide")) : null;
                                                    if (mediaResponse?.display_as === "hide") return null;
                                                    if (!viewpoint?.touchpoint?.suppressed_questions?.includes(item.question) && (item.answers.length > 0 && item.answers[0] !== "")) {
                                                        return (
                                                            <Box key={questionIndex} sx={{ marginBottom: "40px" }}>
                                                                <Typography sx={{
                                                                    fontWeight: "700",
                                                                    fontSize: "1rem",
                                                                    color: "#000000"
                                                                }}>
                                                                    {cleanHtmlQuestion(item.question)}
                                                                </Typography>
                                                                <Box sx={{ borderLeft: "2px solid #ccc", padding: "15px 20px", margin: "15px 0" }}>
                                                                    {mediaResponse ?
                                                                        <MediaAnswer mediaResponse={mediaResponse} touchpoint={viewpoint?.touchpoint || null} advisor={advisor} project={project} /> :
                                                                        <Box sx={{
                                                                            marginTop: "0.25rem",
                                                                            fontSize: "1rem"
                                                                        }}>
                                                                            {Array.isArray(item.answers) && item.answers.length > 1
                                                                                ? <ul style={{ margin: "0", padding: "0", paddingLeft: "1rem" }}>{item.answers.map((answer: string) => <li key={answer} style={{ marginBottom: "0.5rem" }}>{answer}</li>)}</ul>
                                                                                : item.answers}
                                                                        </Box>
                                                                    }
                                                                </Box>
                                                            </Box>
                                                        )
                                                    }
                                                })}
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </>
                        )}
                    </>
                ) : (
                    <Loading />
                )}
            </Box>
            <MeetingRequest project={project} advisor={advisor} open={requestDemo} setOpen={setRequestDemo} updateAdvisor={updateAdvisor} />
        </AdvisorBox>
    );
}

export const CompanyAdvisorModal = ({ uuid, touchpointUuid, onClose, reviewMode = false }: { uuid: string, touchpointUuid: string | null, onClose?: () => void, reviewMode?: boolean | null }) => {
    const [isModalOpen, setIsModalOpen] = useState(uuid ? true : false);
    const { dealContext } = useDealContext();
    const [advisor, setAdvisor] = useState(null);
    const project = dealContext.project;
    const { getAccessTokenSilently } = useAuth0();

    const client = new ProjectHttpClient();


    const fetchAdvisor = async () => {
        const accessToken = await getAccessTokenSilently();
        const { data, error } = await client.getAdvisor(uuid, project.id, accessToken, reviewMode);
        if (error) return;
        setAdvisor(data.advisor);
    }

    useEffect(() => {
        if (!uuid || !project) return;
        fetchAdvisor();
    }, [uuid, project]);

    const handleClose = () => {
        setIsModalOpen(false);
        if (onClose) onClose();
    }

    const handleExited = () => {
        if (onClose) onClose();
        window.location.hash = '';
    }

    if (!project) return null;
    if (!advisor) return null;


    return (
        <Modal
            open={isModalOpen}
            onClose={handleClose}
            sx={{
                display: 'flex',
                alignItems: 'stretch',
                justifyContent: 'flex-end'
            }}
        >
            <Slide direction="left" in={isModalOpen} mountOnEnter unmountOnExit onExited={handleExited}>
                <Box sx={{
                    width: '85%',
                    maxWidth: '1200px',
                    minWidth: '400px',
                    height: '100vh',
                    backgroundColor: 'white',
                    boxShadow: 24,
                    outline: 'none',
                    overflow: 'hidden'
                }}>
                    <AdvisorProfile project={project} advisor={advisor} updateAdvisor={fetchAdvisor} touchpointUuid={touchpointUuid} />
                </Box>
            </Slide>
        </Modal>
    );
}   